import React, {
  FunctionComponent,
  MouseEvent,
  useEffect,
  useRef,
  useMemo,
} from 'react';

import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  every,
  filter,
  find,
  flatMap,
  get,
  isEmpty,
  isNull,
  map,
  orderBy,
  uniq,
} from 'lodash';
import { format } from 'date-fns';
import {
  Box,
  Button,
  CardContent,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { AgGridReact } from 'ag-grid-react';

import { Add, MoreHoriz, PlayArrow } from '@mui/icons-material';
import { blue, green, grey } from '@mui/material/colors';
import { useSnackbar } from 'notistack';
import { ColDef } from 'ag-grid-community';
import { ICellRendererParams } from 'ag-grid-enterprise';

import * as API from '../../services/API';

import { Model, ModelInstance, Profile, UserRole } from '../../types/models';

import { parseId } from '../../utils/parsing';

import StyledMenu from '../shared/StyledMenu';

import useSetState from '../../hooks/useSetState';

import useDynamicCallback from '../../hooks/useDynamicCallback';

import FullScreenProgress from '../shared/FullScreenProgress';

import Table from '../shared/Table';

import { useWindowDimensions } from '../../hooks/useWindowDimensions';

import { useProfile } from '../../hooks/useProfile';

import { useData } from '../../hooks/useData';

import ClientIndex from '../shared/ClientIndex';

import CreateNewReportDialog from './CreateNewReportDialog';
import UpdateReportNameDialog from './UpdateReportNameDialog';
import EditPortfolioSourceType from './PortfolioReportDetails/ReportEdit/EditPortfolioSourceType';
import PortfolioReportDetails from './PortfolioReportDetails';
import DeleteReportDialog from './DeleteReportDialog';
import PortfolioReportGeneratedFileDownload from './PortfolioReportGeneratedFileDownload';
import {
  isChartConfiguredReportType,
  PortfolioReportData,
  PortfolioReportGeneratedFile,
  PortfolioReportMembershipRole,
  PortfolioReportMemberships,
  PortfolioReportPermissions,
  PortfolioReportTypes,
} from './types';
import ReportTemplateDownloadDialog from './ReportTemplateDownloadDialog';
import CloneReportDialog from './CloneReportDialog';

const RECENT_REPORTS_LENGTH = 10;

export const DetailRowRenderer = (
  params: ICellRendererParams<IPortfolioReportStateData>
) => {
  return (
    <Box
      px={2}
      sx={{
        '*': {
          marginTop: 1,
        },
      }}
    >
      {params.data?.Description && (
        <Grid container alignItems="center">
          <Grid md={1.5}>
            <strong>Description:</strong>
          </Grid>
          <Grid md={10.5}>
            <Typography sx={{ whiteSpace: 'normal' }}>
              {params.data?.Description}
            </Typography>
          </Grid>
        </Grid>
      )}
      <Grid container alignItems="center">
        <Grid md={1.5}>
          <strong>Last Updated:</strong>
        </Grid>
        <Grid md={10.5}>
          <Typography sx={{ whiteSpace: 'normal' }}>
            {format(
              new Date(params.data?.updated_at as string),
              'MM/dd/yyyy hh:mm:ss a'
            )}
          </Typography>
        </Grid>
      </Grid>
      {!isEmpty(params.data?.Models) && (
        <Grid container alignItems="center">
          <Grid md={1.5}>
            <strong>Models: </strong>
          </Grid>
          <Grid md={10.5}>
            <List disablePadding dense>
              {map(uniq(params.data?.Models), (model, index) => (
                <ListItem
                  key={`model-${index}`}
                  dense
                  disablePadding
                  disableGutters
                >
                  <ListItemText primary={model} />
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

type ISelectedReportType =
  | 'Recent'
  | 'Private Reports'
  | 'Shared Reports'
  | 'All Reports'
  | 'Viewer'
  | 'Restricted'
  | 'Owner';

export interface IPortfolioReportStateData {
  ClientID: PortfolioReportData['ClientID'];
  Name: PortfolioReportData['Name'];
  Description: PortfolioReportData['Description'];
  ReportPermission: PortfolioReportData['PermissionLevel'];
  ReportMembership: PortfolioReportMemberships['Role'];
  ReportState: PortfolioReportData['State'];
  ReportType: PortfolioReportData['Type'];
  id: PortfolioReportData['id'];
  updated_at: PortfolioReportData['updated_at'];
  updated_by: PortfolioReportData['updated_by'];
  Models: Model['Name'][];
  ModelInstances: ModelInstance['id'][];
  ModelID: PortfolioReportData['ModelID'];
  ModelInstanceID: PortfolioReportData['ModelInstanceID'];
  Metadata: any;
  ReportTemplateURL: PortfolioReportData['ReportTemplateURL'];
  ReportTemplateName: PortfolioReportData['ReportTemplateName'];
  CustomDownloadBlobName: PortfolioReportData['CustomDownloadBlobName'];
  PortfolioDataCuts: PortfolioReportData['PortfolioDataCuts'];
  ReportMemberships?: PortfolioReportData['ReportMemberships'];
  Finalized?: PortfolioReportData['Finalized'];
  Model?: { Name: string };
  ModelInstance?: { Name: string };
  LastRunAt?: string | null;
}

interface IPortfolioState {
  // Dialog state
  isCreateNewReportDialogOpen: boolean;
  isEditDataSourceDialogOpen: boolean;
  isRenameReportDialogOpen: boolean;
  isDownloadTemplateDialogOpen: boolean;
  isDeleteDialogOpen: boolean;
  isCloneDialogOpen: boolean;
  isGeneratedFileDialogOpen: boolean;

  // Report data
  originalReportsData: IPortfolioReportStateData[];
  selectedReportType: ISelectedReportType;
  isPaginationEnabled: boolean;
  isOnlyViewerForAllReports: boolean;

  // Selected items
  selectedReport?: IPortfolioReportStateData;
  generatedFileID?: PortfolioReportGeneratedFile['id'];

  // Navigation
  redirectToReportsPage: boolean;
  runReport: boolean;

  // Form state
  isAnyFieldUpdated: boolean;
}

const initialPortfolioState: IPortfolioState = {
  // Dialog state
  isCreateNewReportDialogOpen: false,
  isEditDataSourceDialogOpen: false,
  isRenameReportDialogOpen: false,
  isDownloadTemplateDialogOpen: false,
  isDeleteDialogOpen: false,
  isCloneDialogOpen: false,
  isGeneratedFileDialogOpen: false,

  // Report data
  originalReportsData: [],
  selectedReportType: 'Recent',
  isPaginationEnabled: false,
  isOnlyViewerForAllReports: false,

  // Selected items
  selectedReport: undefined,
  generatedFileID: undefined,

  // Navigation
  redirectToReportsPage: false,
  runReport: false,

  // Form state
  isAnyFieldUpdated: false,
};

const getFilteredReports = (
  selectedReportType: ISelectedReportType,
  originalReportsData: IPortfolioReportStateData[]
): IPortfolioReportStateData[] => {
  switch (selectedReportType) {
    case 'Recent':
      return orderBy(originalReportsData, ['updated_at'], ['desc']).slice(
        0,
        RECENT_REPORTS_LENGTH
      );
    case 'Private Reports':
      return filter(orderBy(originalReportsData, ['updated_at'], ['desc']), {
        ReportPermission: PortfolioReportPermissions.Private,
      });
    case 'Shared Reports':
      return filter(orderBy(originalReportsData, ['updated_at'], ['desc']), {
        ReportPermission: PortfolioReportPermissions.Shared,
      });
    case 'All Reports':
      return orderBy(originalReportsData, ['updated_at'], ['desc']);
    case 'Viewer':
      return filter(orderBy(originalReportsData, ['updated_at'], ['desc']), {
        ReportMembership: PortfolioReportMembershipRole.Viewer,
      });
    case 'Owner':
      return filter(orderBy(originalReportsData, ['updated_at'], ['desc']), {
        ReportMembership: PortfolioReportMembershipRole.Owner,
      });
    case 'Restricted':
      return filter(orderBy(originalReportsData, ['updated_at'], ['desc']), {
        ReportMembership: PortfolioReportMembershipRole.Restricted,
      });
  }
};

const PortfolioDashboard: FunctionComponent = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const gridRef = useRef<AgGridReact<IPortfolioReportStateData>>(null);
  const { width } = useWindowDimensions();

  const { enqueueSnackbar } = useSnackbar();

  const [state, setState] = useSetState<IPortfolioState>(initialPortfolioState);

  const profile: Profile = useProfile();

  const isClientReadOnlyUser =
    profile.User.Role === UserRole.Client_Read_Only_User;
  const location = useLocation();
  const { search } = location;
  const params = (): { clientId: undefined | number } => {
    const params = new URLSearchParams(search);
    const clientId =
      profile && profile.User.ClientID !== null
        ? profile.User.ClientID
        : parseId(params.get('client'));

    return { clientId };
  };

  const { data, refresh, loading } = useData<{
    report?: PortfolioReportData[];
    models?: Model[];
  }>(
    () => ({
      report:
        params().clientId !== undefined
          ? `/clients/${params().clientId}/reports`
          : undefined,
      models:
        params().clientId !== undefined
          ? `/clients/${params().clientId}/models`
          : undefined,
    }),
    [params().clientId]
  );
  // useEffect(() => {
  //   refresh();
  // }, []);

  useEffect(() => {
    if (!isEmpty(data && data.report)) {
      updateReportsData();
    } else {
      setState({
        originalReportsData: [],
      });
    }
  }, [data.report]);

  const updateReportsData = async () => {
    const { reportsData }: { reportsData: IPortfolioReportStateData[] } =
      await getReportsData();

    const sortedData = orderBy(reportsData, ['updated_at'], ['desc']);

    setState({
      originalReportsData: sortedData,
      ...(!isEmpty(state.selectedReport) && {
        selectedReport: find(reportsData, {
          id: get(state.selectedReport, 'id'),
        }),
      }),
      isOnlyViewerForAllReports:
        every(reportsData, {
          ReportMembership: PortfolioReportMembershipRole.Viewer,
        }) || isClientReadOnlyUser,
    });
  };

  const getReportsData = async (): Promise<{
    reportsData: IPortfolioReportStateData[];
  }> => {
    const results = await Promise.all(
      map(data?.report, async (item) => {
        return {
          report: {
            Name: item.Name,
            Description: item.Description,
            CustomDownloadBlobName: item.CustomDownloadBlobName,
            ReportTemplateName: item.ReportTemplateName,
            ReportTemplateURL: item.ReportTemplateURL,
            ReportPermission: item.PermissionLevel,
            ReportMembership: (
              find(item.ReportMemberships, {
                UserID: profile.User.id,
              }) as PortfolioReportMemberships
            ).Role,
            LastRunAt: (
              find(item.ReportMemberships, {
                UserID: profile.User.id,
              }) as PortfolioReportMemberships
            ).LastRunAt,
            ReportState: item.State,
            ReportType: item.Type,
            id: item.id,
            updated_at: item.updated_at,
            updated_by: {
              Display_Name: item.updated_by.Display_Name,
              User_Name: item.updated_by.User_Name,
            },
            Models: map(
              map(flatMap(item, 'PortfolioDataCuts'), 'Models'),
              'Name'
            ),
            ModelInstances: (() => {
              const datacuts = flatMap(item, 'PortfolioDataCuts');
              return map(
                flatMap(datacuts, 'MetaData'),
                'ModelInstance'
              ) as ModelInstance['id'][];
            })(),
            DataSourceID: state.selectedReport?.id ?? 2,
            ModelID: item.ModelID,
            ModelInstanceID: item.ModelInstanceID,
            ClientID: item.ClientID,
            Metadata: item.Metadata,
            PortfolioDataCuts: item.PortfolioDataCuts ?? [],
            Model: item.Model,
            ModelInstance: item.ModelInstance,
          },
        };
      })
    );
    return {
      reportsData: results.map((r) => r.report),
    };
  };

  // Compute filtered data based on selected report type (memoized)
  const filteredReports = useMemo(() => {
    return getFilteredReports(
      state.selectedReportType,
      state.originalReportsData
    );
  }, [state.selectedReportType, state.originalReportsData]);

  const handleOpenCreateNewReport = (): void => {
    setState({ isCreateNewReportDialogOpen: true, selectedReport: undefined });
  };

  const handleCloseCreateNewReport = (): void => {
    setState({ isCreateNewReportDialogOpen: false });
  };

  const handleEditDataSource = async (
    row: IPortfolioReportStateData
  ): Promise<void> => {
    // setState({
    //   isEditDataSourceDialogOpen: true,
    //
    //   selectedReport: row,
    // });
    // await onClickReport(row);
    onClickReportDetails(row);
  };

  const handleCloseEditDataSourceModal = (): void => {
    setState({
      isEditDataSourceDialogOpen: false,
      selectedReport: undefined,
    });
  };

  const handleOpenRenameReport = (row: IPortfolioReportStateData): void => {
    setState({
      isRenameReportDialogOpen: true,
      selectedReport: row,
    });
  };

  const handleOpenDeleteReport = (row: IPortfolioReportStateData): void => {
    setState({
      isDeleteDialogOpen: true,
      selectedReport: row,
    });
  };

  const handleCloneReport = (row: IPortfolioReportStateData): void => {
    setState({
      isCloneDialogOpen: true,
      selectedReport: row,
    });
  };

  const handleOpenReportTemplateDownload = (
    row: IPortfolioReportStateData
  ): void => {
    setState({
      isDownloadTemplateDialogOpen: true,
      selectedReport: row,
    });
  };

  const handleCloseRenameReport = (): void => {
    setState({
      isRenameReportDialogOpen: false,
    });
  };

  const handleCloseReportTemplateDownload = (): void => {
    setState({
      isDownloadTemplateDialogOpen: false,
    });
  };

  const handleRunReport = async (
    report: IPortfolioReportStateData
  ): Promise<void> => {
    if (report.ReportType === 'Portfolio') {
      try {
        const result = await API.update<PortfolioReportData>(
          `/reports/${report.id}/portfolio_report_generated_files`,
          {
            FileName: `${report.Name}_${format(
              new Date(),
              'yyyy-MM-ddTHH:MM'
            )}.xlsx`,
          }
        );
        setState({
          generatedFileID: result.id,
          isGeneratedFileDialogOpen: true,
          selectedReport: report,
        });
      } catch (e) {
        enqueueSnackbar((e as any).body.message, {
          variant: 'error',
        });
      }
    }
    if (report.ReportType !== 'Portfolio') {
      searchParams.set('reportId', report.id.toString());
      searchParams.set('runReport', 'true');
      setSearchParams(searchParams);
    }
    setState({
      redirectToReportsPage: report.ReportType !== 'Portfolio',
      selectedReport: report,
      runReport: report.ReportType !== 'Portfolio',
    });
  };

  const clientId =
    profile && profile.User.ClientID !== null
      ? profile.User.ClientID
      : parseId(searchParams.get('client'));

  const { data: selectedReport, refresh: refreshReport } = useData<{
    reportData?: IPortfolioReportStateData;
  }>(
    () => ({
      reportData:
        state.selectedReport && state.selectedReport!.id !== undefined
          ? `/clients/${clientId}/reports/${state.selectedReport?.id}`
          : undefined,
    }),
    [state.selectedReport]
  );

  const isNotValidPortfolioReport = (
    report: IPortfolioReportStateData
  ): boolean => {
    const checkForInvalidScenarios = (): boolean => {
      let inValidScenariosCount = 0;
      report.PortfolioDataCuts.forEach((f) => {
        if (f.MetaData.NumScenarios !== f.MetaData.Scenarios.length) {
          inValidScenariosCount = inValidScenariosCount + 1;
        }
        f.MetaData.Scenarios.forEach((item) => {
          if (item === null) {
            inValidScenariosCount = inValidScenariosCount + 1;
          }
        });
      });
      return inValidScenariosCount > 0;
    };
    const isInvalidPortfolioReport =
      report?.ReportType === 'Portfolio' &&
      (report.PortfolioDataCuts.length === 0 ||
        (report.PortfolioDataCuts.length > 0 && checkForInvalidScenarios()));

    return isInvalidPortfolioReport;
  };

  const isReportTypeWaterfallOrDriver = (reportType: PortfolioReportTypes) =>
    isChartConfiguredReportType(reportType);

  const actionsFormatter = useDynamicCallback(
    (params: ICellRendererParams<IPortfolioReportStateData>): JSX.Element => {
      return (
        <>
          <IconButton
            onClick={() =>
              handleRunReport(params.data as IPortfolioReportStateData)
            }
            disabled={
              (isNull(params.data?.Metadata) &&
                isReportTypeWaterfallOrDriver(params.data?.ReportType)) ||
              (!isNull(params.data.Metadata) &&
                isReportTypeWaterfallOrDriver(params.data?.ReportType) &&
                !isEmpty(params.data.Metadata.WaterfallChart) &&
                (isNull(params.data.Metadata?.WaterfallChart?.ScenarioId_A) ||
                  isNull(
                    params.data.Metadata?.WaterfallChart?.ScenarioId_B
                  ))) ||
              isNotValidPortfolioReport(params.data) ||
              (!isNull(params.data.Metadata) &&
                isReportTypeWaterfallOrDriver(params.data?.ReportType) &&
                !isEmpty(params.data.Metadata.DriverAnalysisData) &&
                (isNull(
                  params.data.Metadata?.DriverAnalysisData?.ScenarioId_A
                ) ||
                  isNull(
                    params.data.Metadata?.DriverAnalysisData?.ScenarioId_B
                  )))
            }
            {...((isNull(params.data?.Metadata) &&
              isReportTypeWaterfallOrDriver(params.data?.ReportType)) ||
            isNotValidPortfolioReport(params.data)
              ? { title: 'You cannot run a report which has no metadata.' }
              : { title: 'Run Report' })}
            sx={{
              color:
                isNull(params.data?.Metadata) &&
                (params.data?.ReportType === PortfolioReportTypes.Waterfall ||
                  params.data?.ReportType === PortfolioReportTypes.Tornado)
                  ? grey[500]
                  : green[500],
            }}
          >
            <PlayArrow />
          </IconButton>
          {!isClientReadOnlyUser && (
            <>
              {params.data?.ReportMembership ===
                PortfolioReportMembershipRole.Owner && (
                <PopupState
                  variant="popover"
                  popupId={`report-settings-menu-${params.data?.id}`}
                >
                  {(actionsPopupState) => {
                    const trigger = bindTrigger(actionsPopupState);

                    return (
                      <>
                        <IconButton
                          title="More actions"
                          aria-label="More actions"
                          {...trigger}
                          onClick={(e: MouseEvent) => {
                            e.stopPropagation();
                            trigger.onClick(e);
                          }}
                        >
                          <MoreHoriz />
                        </IconButton>
                        <StyledMenu {...bindMenu(actionsPopupState)}>
                          <MenuItem
                            onClick={() => {
                              actionsPopupState.close();

                              handleEditDataSource(
                                params.data as IPortfolioReportStateData
                              );
                            }}
                          >
                            Edit
                          </MenuItem>
                          <MenuItem
                            onClick={() => {
                              handleOpenRenameReport(
                                params.data as IPortfolioReportStateData
                              );
                            }}
                          >
                            Rename
                          </MenuItem>
                          <MenuItem
                            onClick={() => {
                              actionsPopupState.close();

                              handleCloneReport(
                                params.data as IPortfolioReportStateData
                              );
                            }}
                          >
                            Clone
                          </MenuItem>
                          {(params.data?.ReportType === 'Portfolio' ||
                            params.data?.ReportType === 'Waterfall') && (
                            <MenuItem
                              onClick={() => {
                                actionsPopupState.close();

                                handleOpenReportTemplateDownload(
                                  params.data as IPortfolioReportStateData
                                );
                              }}
                            >
                              Manage Report Template
                            </MenuItem>
                          )}
                          <MenuItem
                            onClick={() => {
                              actionsPopupState.close();

                              handleOpenDeleteReport(
                                params.data as IPortfolioReportStateData
                              );
                            }}
                          >
                            Delete
                          </MenuItem>
                        </StyledMenu>
                      </>
                    );
                  }}
                </PopupState>
              )}
            </>
          )}
        </>
      );
    }
  );

  const onClickReportDetails = (row: IPortfolioReportStateData): void => {
    searchParams.set('reportId', row.id.toString());
    searchParams.set('runReport', 'false');
    setSearchParams(searchParams);
    setState({ redirectToReportsPage: true, selectedReport: row });
  };

  const ModelRenderer = useDynamicCallback(
    (cellRendererParams: ICellRendererParams<IPortfolioReportStateData>) => {
      return cellRendererParams.data?.Model?.Name ? (
        <Typography
          component="span"
          variant="body2"
          sx={{
            textWrap: 'auto',
            WebkitLineClamp: 3,
            textOverflow: 'ellipsis',
            // maxHeight: 'calc(1.57em*3)',
            overflow: 'hidden',
            display: 'inline-flex',
            alignItems: 'center',
            height: '100%',
            marginTop: '8px',
          }}
        >
          {cellRendererParams.data?.Model?.Name}
        </Typography>
      ) : (
        ''
      );
    }
  );

  const reportNameFormatter = useDynamicCallback(
    (
      params: ICellRendererParams<IPortfolioReportStateData>
    ): JSX.Element | null => {
      if (
        isClientReadOnlyUser ||
        (params.data?.ReportMembership ===
          PortfolioReportMembershipRole.Restricted &&
          !isClientReadOnlyUser)
      ) {
        return (
          <Box>
            <Typography
              component="span"
              variant="body2"
              sx={{
                textWrap: 'auto',
                WebkitLineClamp: 3,
                textOverflow: 'ellipsis',
                maxHeight: 'calc(1.57em*3)',
                overflow: 'hidden',
                display: 'inline-flex',
                alignItems: 'center',
                height: '100%',
                whiteSpace: 'pre-wrap',
              }}
            >
              {params.value}
            </Typography>
          </Box>
        );
      } else if (!isClientReadOnlyUser) {
        if (
          params.data?.ReportMembership ===
            PortfolioReportMembershipRole.Owner ||
          params.data?.ReportMembership === PortfolioReportMembershipRole.Viewer
        ) {
          return (
            <Box>
              <Typography
                component="span"
                variant="body2"
                title={`Navigate to ${params.value} details`}
                sx={{
                  textWrap: 'auto',
                  WebkitLineClamp: 3,
                  textOverflow: 'ellipsis',
                  maxHeight: 'calc(1.57em*3)',
                  overflow: 'hidden',
                  display: 'inline-flex',
                  alignItems: 'center',
                  height: '100%',
                  whiteSpace: 'pre-wrap',
                }}
              >
                <u
                  style={{ color: blue[500], cursor: 'pointer' }}
                  onClick={() =>
                    onClickReportDetails(
                      params.data as IPortfolioReportStateData
                    )
                  }
                >
                  {params.value}{' '}
                </u>
              </Typography>
            </Box>
          );
        }
      }
      return null;
    }
  );

  const getRowStyle = (p: any) => {
    if (p.data?.ReportMembership === PortfolioReportMembershipRole.Restricted) {
      return { backgroundColor: grey[200] };
    }
    return undefined;
  };

  const lastEditedAtFormatter = useDynamicCallback(
    (params: ICellRendererParams<IPortfolioReportStateData>): JSX.Element => {
      return (
        <Box>
          <Tooltip title={params.data?.updated_by.Display_Name as string} arrow>
            <Typography component="span" variant="body2">
              {format(new Date(params.value), 'MM/dd/yyyy hh:mm:ss a')}
            </Typography>
          </Tooltip>
        </Box>
      );
    }
  );

  const lastRunAtFormatter = useDynamicCallback(
    (params: ICellRendererParams<IPortfolioReportStateData>): JSX.Element => {
      return (
        <Box>
          <Typography component="span" variant="body2">
            {params?.data?.LastRunAt
              ? format(
                  new Date(params?.data?.LastRunAt),
                  'MM/dd/yyyy hh:mm:ss a'
                )
              : '-'}
          </Typography>
        </Box>
      );
    }
  );

  const sizeToFit = () => {
    gridRef.current?.api.sizeColumnsToFit();
  };

  const reportColumns: ColDef<IPortfolioReportStateData>[] = useMemo(() => {
    return [
      {
        field: 'Name',
        headerName: 'Report Name',
        sortable: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          suppressAndOrCondition: true,
        },
        menuTabs: ['filterMenuTab'],
        cellRenderer: reportNameFormatter,
        minWidth: 245,
        maxWidth: 1000,
      },
      {
        field: 'ReportType',
        headerName: 'Type',
        sortable: true,
        filter: 'agSetColumnFilter',
        menuTabs: ['filterMenuTab'],
        minWidth: 130,
        maxWidth: 140,
      },
      // {
      //   field: 'Models',
      //   headerName: 'Models',
      //   cellRenderer: modelsFormatter,
      //   filter: 'agMultiColumnFilter',
      //   flex: 2,
      // },
      {
        field: 'ModelID',
        headerName: 'Model',
        cellRenderer: ModelRenderer,
        filter: 'agSetColumnFilter',
        menuTabs: [],
        minWidth: 100,
        maxWidth: 330,
      },
      {
        field: 'LastRunAt',
        headerName: 'Last Run At',
        cellRenderer: lastRunAtFormatter,
        sortable: true,
        filter: 'agSetColumnFilter',
        menuTabs: [],
        comparator: (valueA: any, valueB: any) => {
          if (!valueA) return 1;
          if (!valueB) return -1;
          return new Date(valueA).getTime() - new Date(valueB).getTime(); // Sort chronologically
        },
        minWidth: 200,
        maxWidth: 300,
      },
      {
        field: 'updated_at',
        headerName: 'Last Edited At',
        filter: 'agSetColumnFilter',
        menuTabs: [],
        cellRenderer: lastEditedAtFormatter,
        sortable: true,
        minWidth: 200,
        maxWidth: 300,
      },
      {
        field: 'id',
        headerName: 'Actions',
        filter: 'agSetColumnFilter',
        menuTabs: [],
        cellRenderer: actionsFormatter,
        sortable: false,
        minWidth: 100,
        maxWidth: 150,
      },
    ];
  }, [
    reportNameFormatter,
    ModelRenderer,
    lastRunAtFormatter,
    lastEditedAtFormatter,
    actionsFormatter,
  ]);

  useEffect(() => {
    if (
      (searchParams.get('reportId') !== undefined ||
        searchParams.get('runReport') !== undefined) &&
      state.redirectToReportsPage === false
    ) {
      searchParams.delete('reportId');
      searchParams.delete('runReport');
      setSearchParams(searchParams);
    }
  }, [searchParams, state.redirectToReportsPage]);

  const handleCloseReportDetails = (): void => {
    setState({
      redirectToReportsPage: false,
      selectedReport: undefined,
    });
    searchParams.delete('reportId');
    searchParams.delete('runReport');
    setSearchParams(searchParams);
    refresh();
  };

  const closeGeneratedFile = (): void => {
    setState({
      generatedFileID: undefined,
      isGeneratedFileDialogOpen: false,
    });
    refresh();
  };

  const isThereAnUnsavedComponent = (isAnyFieldUpdated: boolean) =>
    setState({ isAnyFieldUpdated });

  const handleClientChange = (id?: number) => {
    if (id === params().clientId) {
      return;
    }

    if (id === undefined) {
      searchParams.delete('client');
    } else {
      searchParams.set('client', id.toString());
    }
    searchParams.delete('model');
    searchParams.delete('instance');
    searchParams.delete('scenario');
    searchParams.delete('source');
    setSearchParams(searchParams);
  };

  useEffect(() => {
    searchParams.delete('reportId');
    searchParams.delete('runReport');
    setSearchParams(searchParams);
  }, []);

  if (params().clientId === undefined) {
    return <ClientIndex onSelect={handleClientChange} />;
  }

  if (state.redirectToReportsPage) {
    return (
      <PortfolioReportDetails
        selectedReport={state.selectedReport}
        onClose={handleCloseReportDetails}
        clientId={params().clientId}
        refreshReports={refresh}
        runReport={handleRunReport}
        generatedFileID={state.generatedFileID}
        isGeneratedFileDialogOpen={state.isGeneratedFileDialogOpen}
        onCloseGeneratedFileDialog={closeGeneratedFile}
        models={data.models || []}
      />
    );
  } else {
    return (
      <Box>
        <PortfolioReportGeneratedFileDownload
          progressMsg="Preparing download"
          onClose={closeGeneratedFile}
          generatedFileId={state.generatedFileID}
          isOpen={state.isGeneratedFileDialogOpen}
          reportData={state.selectedReport}
        />
        <CreateNewReportDialog
          isOpen={state.isCreateNewReportDialogOpen}
          onClose={handleCloseCreateNewReport}
          refreshReports={refresh}
          clientId={params().clientId}
          reportNames={map(filteredReports, 'Name')}
          reportName={
            !isEmpty(state.selectedReport)
              ? (state.selectedReport as IPortfolioReportStateData).Name
              : ''
          }
        />
        {loading ? (
          <FullScreenProgress />
        ) : (
          <>
            <Grid container>
              <Grid md={12} sm={12}>
                <CardContent>
                  <Grid
                    container
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Grid md={4}>
                      <Typography variant="h4">
                        {state.selectedReportType}
                      </Typography>
                      {state.selectedReportType === 'Recent' && (
                        <Typography>10 results</Typography>
                      )}
                    </Grid>
                    <Grid md={2}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Add />}
                        fullWidth
                        onClick={handleOpenCreateNewReport}
                        disabled={
                          profile.User.Role === UserRole.Client_Read_Only_User
                        }
                      >
                        Create New Report
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Grid>
            </Grid>
            <Grid container>
              <Grid
                xs={2}
                sx={{
                  borderRadius: `0px 0px 0px 3px`,
                  borderRight: '1px solid rgba(0, 0, 0, 0.12)',
                }}
              >
                <CardContent>
                  <Typography variant={width > 1600 ? 'h5' : 'h6'}>
                    Reports
                  </Typography>
                  <List>
                    <ListItem disablePadding>
                      <ListItemButton
                        selected={state.selectedReportType === 'Recent'}
                        onClick={() => {
                          setState({
                            selectedReportType: 'Recent',
                            isPaginationEnabled: false,
                          });
                        }}
                      >
                        <ListItemText primary="Recent" />
                      </ListItemButton>
                    </ListItem>
                    <Divider />

                    <ListItem disablePadding>
                      <ListItemButton
                        selected={
                          state.selectedReportType === 'Private Reports'
                        }
                        onClick={() => {
                          setState({
                            selectedReportType: 'Private Reports',
                            isPaginationEnabled: true,
                          });
                        }}
                      >
                        <ListItemText primary="Private Reports" />
                      </ListItemButton>
                    </ListItem>
                    <Divider />

                    <ListItem disablePadding>
                      <ListItemButton
                        selected={state.selectedReportType === 'Shared Reports'}
                        onClick={() => {
                          setState({
                            selectedReportType: 'Shared Reports',
                            isPaginationEnabled: true,
                          });
                        }}
                      >
                        <ListItemText primary="Shared Reports" />
                      </ListItemButton>
                    </ListItem>
                    <Divider />
                    <ListItem disablePadding>
                      <ListItemButton
                        selected={state.selectedReportType === 'All Reports'}
                        onClick={() => {
                          setState({
                            selectedReportType: 'All Reports',
                            isPaginationEnabled: true,
                          });
                        }}
                      >
                        <ListItemText primary="All Reports" />
                      </ListItemButton>
                    </ListItem>
                    <Divider />
                  </List>
                  {!state.isOnlyViewerForAllReports && (
                    <>
                      <Typography
                        variant={width > 1600 ? 'h5' : 'h6'}
                        sx={{ pt: 2 }}
                      >
                        By Permission
                      </Typography>
                      <List>
                        <ListItem disablePadding>
                          <ListItemButton
                            onClick={() => {
                              setState({
                                selectedReportType: 'Viewer',
                                isPaginationEnabled: true,
                              });
                            }}
                            selected={state.selectedReportType === 'Viewer'}
                          >
                            <ListItemText primary="Viewer" />
                          </ListItemButton>
                        </ListItem>
                        <Divider />
                        <ListItem disablePadding>
                          <ListItemButton
                            selected={state.selectedReportType === 'Owner'}
                            onClick={() => {
                              setState({
                                selectedReportType: 'Owner',
                                isPaginationEnabled: true,
                              });
                            }}
                          >
                            <ListItemText primary="Owner" />
                          </ListItemButton>
                        </ListItem>
                        <Divider />
                        <ListItem disablePadding>
                          <ListItemButton
                            selected={state.selectedReportType === 'Restricted'}
                            onClick={() => {
                              setState({
                                selectedReportType: 'Restricted',
                                isPaginationEnabled: true,
                              });
                            }}
                          >
                            <ListItemText primary="Restricted" />
                          </ListItemButton>
                        </ListItem>
                      </List>
                    </>
                  )}
                </CardContent>
              </Grid>
              <Grid xs={10} sx={{ borderRadius: `0px 0px 0px 3px`, pb: 8 }}>
                {state.isPaginationEnabled && (
                  <Table
                    defaultColDef={{
                      autoHeight: true,
                      floatingFilter: false,
                    }}
                    key={state.selectedReportType}
                    gridRef={gridRef}
                    id="reports-table"
                    isLoading={false}
                    isSuccess
                    pagination={state.isPaginationEnabled}
                    rowCount={filteredReports.length}
                    rowData={filteredReports}
                    columnDefs={reportColumns}
                    suppressRowClickSelection
                    suppressPaginationPanel
                    onGridReady={sizeToFit}
                    masterDetail
                    gridOptions={{
                      isRowMaster: (data: IPortfolioReportStateData) =>
                        isClientReadOnlyUser ||
                        (data?.ReportMembership ===
                          PortfolioReportMembershipRole.Viewer &&
                          !isClientReadOnlyUser),
                    }}
                    detailCellRenderer={DetailRowRenderer}
                    getRowStyle={getRowStyle}
                  />
                )}
                {!state.isPaginationEnabled && (
                  <Table
                    defaultColDef={{
                      autoHeight: true,
                      floatingFilter: false,
                    }}
                    gridRef={gridRef}
                    id="reports-table"
                    isLoading={false}
                    isSuccess
                    pagination={false}
                    rowCount={filteredReports.length}
                    rowData={filteredReports}
                    columnDefs={reportColumns}
                    suppressRowClickSelection
                    suppressPaginationPanel
                    onGridReady={sizeToFit}
                    masterDetail
                    gridOptions={{
                      isRowMaster: (data: IPortfolioReportStateData) =>
                        isClientReadOnlyUser ||
                        (data?.ReportMembership ===
                          PortfolioReportMembershipRole.Viewer &&
                          !isClientReadOnlyUser),
                    }}
                    detailCellRenderer={DetailRowRenderer}
                    getRowStyle={getRowStyle}
                  />
                )}
              </Grid>
            </Grid>
          </>
        )}
        {state.isEditDataSourceDialogOpen && (
          <EditPortfolioSourceType
            isOpen={state.isEditDataSourceDialogOpen}
            onClose={handleCloseEditDataSourceModal}
            clientId={params().clientId}
            showComponentInADialog
            selectedReport={state.selectedReport}
            refreshReports={refresh}
            isThereAnUnsavedComponent={isThereAnUnsavedComponent}
          />
        )}
        <UpdateReportNameDialog
          isOpen={state.isRenameReportDialogOpen}
          onClose={handleCloseRenameReport}
          reportName={
            state.selectedReport &&
            (state.selectedReport as IPortfolioReportStateData).Name
          }
          reportData={selectedReport.reportData}
          refreshReports={refresh}
          clientId={params().clientId}
          refreshReport={refreshReport}
        />
        <CloneReportDialog
          isOpen={state.isCloneDialogOpen}
          onClose={() => {
            setState({ isCloneDialogOpen: false, selectedReport: undefined });
          }}
          reportData={state.selectedReport}
          refreshReports={refresh}
          clientId={params().clientId}
        />
        <DeleteReportDialog
          isOpen={state.isDeleteDialogOpen}
          onClose={() => {
            setState({ isDeleteDialogOpen: false, selectedReport: undefined });
          }}
          reportData={state.selectedReport}
          refreshReports={refresh}
          clientId={params().clientId}
        />
        <ReportTemplateDownloadDialog
          open={state.isDownloadTemplateDialogOpen}
          close={handleCloseReportTemplateDownload}
          data={state.selectedReport as IPortfolioReportStateData}
          refresh={refresh}
          reportType={state.selectedReport?.ReportType || ''}
        />
      </Box>
    );
  }
};

export default PortfolioDashboard;
